import { Component, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

// import { AuthService } from '../../../auth/services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '../../../auth/services/auth.service';
import { EmployerAccessService } from '../../../auth/services/employer-access.service';
import { filter, Subscription } from 'rxjs';


@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [RouterModule, MatIconModule, MatMenuModule, MatButtonModule],
})
export class HeaderComponent implements OnInit, OnDestroy {
  readonly hasEmployerAccess = signal(false);
  private readonly employerAccessRefreshMs = 60_000;
  private isLoadingEmployerAccess = false;
  private routerSubscription?: Subscription;
  private refreshTimer?: ReturnType<typeof setInterval>;
  private destroyed = false;

  constructor(
    public authService: AuthService,
    private msal: MsalService,
    private employerAccessService: EmployerAccessService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.loadEmployerAccess();
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.loadEmployerAccess();
      });

    // Keep access flags fresh during long sessions on unguarded routes.
    this.refreshTimer = setInterval(() => {
      this.loadEmployerAccess();
    }, this.employerAccessRefreshMs);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.routerSubscription?.unsubscribe();
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  private async loadEmployerAccess() {
    if (this.isLoadingEmployerAccess) {
      return;
    }

    this.isLoadingEmployerAccess = true;
    const activeAccount = this.msal.instance.getActiveAccount();
    const cachedAccounts = this.msal.instance.getAllAccounts();

    if (!activeAccount && cachedAccounts.length === 0) {
      this.setHasEmployerAccess(false);
      this.isLoadingEmployerAccess = false;
      return;
    }

    if (!activeAccount && cachedAccounts.length > 0) {
      this.msal.instance.setActiveAccount(cachedAccounts[0]);
    }

    try {
      const hasAccess = await this.employerAccessService.ensureHasEmployerAccess();
      this.setHasEmployerAccess(hasAccess);
    } catch {
      this.setHasEmployerAccess(false);
    } finally {
      this.isLoadingEmployerAccess = false;
    }
  }

  private setHasEmployerAccess(value: boolean): void {
    if (this.destroyed) {
      return;
    }

    this.ngZone.run(() => {
      this.hasEmployerAccess.set(value);
    });
  }


  getInitials(name: string | null): string {
    if (!name) return '';
    const parts = name.split(' ');
    return parts
      .map((p) => p[0])
      .join('')
      .toUpperCase();
  }

  goToUserProfile(): void {
    void this.router.navigateByUrl('/user-profile');
  }


  async logout() {
    await this.authService.logout();
  }

}
